/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Ek alan tanımlarının uzak API'den yüklenme durumunu açık ve
 * fail-closed bir sözleşmeyle sunar. Ürün formları `ready` olmadan
 * kaydetmeyi açmamalıdır.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  loadEntityCustomFieldDefinitions,
  type EntityCustomFieldDefinition,
} from '../utils/entity-custom-field-client';

export type EntityCustomFieldLoadStatus = 'disabled' | 'loading' | 'ready' | 'error';
export type EntityCustomFieldDefinitionSource = 'disabled' | 'provided' | 'remote';

export interface EntityCustomFieldDefinitionLoadState {
  definitions: readonly EntityCustomFieldDefinition[];
  status: EntityCustomFieldLoadStatus;
  source: EntityCustomFieldDefinitionSource;
  loading: boolean;
  ready: boolean;
  error: string | null;
  retry: () => void;
}

export interface UseEntityCustomFieldDefinitionsOptions {
  entityType?: string | null;
  companyId?: string | number | bigint | null;
  /** `false` ise uzak istek yapılmaz ve durum kaydetmek için hazır kabul edilir. */
  enabled?: boolean;
  /** Tanımları kendisi yöneten ürünler uzak yüklemeyi bununla devre dışı bırakır. */
  providedDefinitions?: readonly EntityCustomFieldDefinition[];
}

interface RemoteDefinitionState {
  requestKey: string;
  status: 'loading' | 'ready' | 'error';
  definitions: readonly EntityCustomFieldDefinition[];
  error: string | null;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  const message = String(error ?? '').trim();
  return message || 'Ek alan tanımları yüklenemedi';
}

export function getEntityCustomFieldDefinitionRequestKey(
  entityType: string,
  companyId?: string | number | bigint | null,
): string {
  const companyScope = companyId === undefined || companyId === null || companyId === ''
    ? 'global'
    : String(companyId);
  return `${entityType.trim()}::${companyScope}`;
}

export function useEntityCustomFieldDefinitions({
  entityType,
  companyId,
  enabled = true,
  providedDefinitions,
}: UseEntityCustomFieldDefinitionsOptions): EntityCustomFieldDefinitionLoadState {
  const normalizedEntityType = typeof entityType === 'string' ? entityType.trim() : '';
  const hasProvidedDefinitions = Array.isArray(providedDefinitions);
  const [retryRevision, setRetryRevision] = useState(0);
  const scopeRequestKey = getEntityCustomFieldDefinitionRequestKey(normalizedEntityType, companyId);
  // Retry tıklandığı anda eski `ready` sonucu geçersizleşsin; effect'in bir sonraki
  // turda loading yazmasını bekleyen tek-render'lık kaydetme penceresi oluşmasın.
  const requestKey = `${scopeRequestKey}::${retryRevision}`;
  const [remoteState, setRemoteState] = useState<RemoteDefinitionState>({
    requestKey: '',
    status: 'loading',
    definitions: [],
    error: null,
  });

  const retry = useCallback(() => {
    setRetryRevision((revision) => revision + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !normalizedEntityType || hasProvidedDefinitions) {
      // Uzak kaynak kapatıldığında önceki başarılı sonucu cache gibi taşımayın.
      // Aynı kapsam daha sonra yeniden açılırsa ilk render'dan itibaren loading olur.
      setRemoteState((current) => (
        current.requestKey === ''
          && current.status === 'loading'
          && current.definitions.length === 0
          && current.error === null
          ? current
          : {
              requestKey: '',
              status: 'loading',
              definitions: [],
              error: null,
            }
      ));
      return undefined;
    }

    const controller = new AbortController();
    setRemoteState({
      requestKey,
      status: 'loading',
      definitions: [],
      error: null,
    });

    void loadEntityCustomFieldDefinitions({
      entityType: normalizedEntityType,
      companyId,
      signal: controller.signal,
    })
      .then((definitions) => {
        if (controller.signal.aborted) return;
        setRemoteState({
          requestKey,
          status: 'ready',
          definitions,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setRemoteState({
          requestKey,
          status: 'error',
          definitions: [],
          error: errorMessage(error),
        });
      });

    return () => controller.abort();
  }, [companyId, enabled, hasProvidedDefinitions, normalizedEntityType, requestKey]);

  return useMemo(() => {
    // Explicit tanımlar eski FormBuilder sözleşmesinde entityType olmadan da
    // kullanılabiliyordu; bu yol uzak kaynaktan bağımsız ve geriye uyumludur.
    if (hasProvidedDefinitions) {
      return {
        definitions: providedDefinitions ?? [],
        status: 'ready',
        source: 'provided',
        loading: false,
        ready: true,
        error: null,
        retry,
      };
    }

    if (!enabled || !normalizedEntityType) {
      return {
        definitions: [],
        status: 'disabled',
        source: 'disabled',
        loading: false,
        ready: true,
        error: null,
        retry,
      };
    }

    const currentState = remoteState.requestKey === requestKey
      ? remoteState
      : {
          requestKey,
          status: 'loading' as const,
          definitions: [],
          error: null,
        };
    return {
      definitions: currentState.status === 'ready' ? currentState.definitions : [],
      status: currentState.status,
      source: 'remote',
      loading: currentState.status === 'loading',
      ready: currentState.status === 'ready',
      error: currentState.error,
      retry,
    };
  }, [enabled, hasProvidedDefinitions, normalizedEntityType, providedDefinitions, remoteState, requestKey, retry]);
}
